import { z } from 'zod';
import { zI18nKey, zMiniGameId } from './ids';
import { zRewards } from './progression';

/** Mini game definitions (content-schemas §7, values from docs/design/minigames.md §0–§4). */
export const zMiniGameLevel = z.object({
  level: z.number().int().min(1),
  nameKey: zI18nKey,
  /** Score at or above this counts as clearing the level (first-clear rewards). */
  passScore: z.number().min(0).max(1),
  firstClear: zRewards,
  /** Repeat-play note pool (i18n keys), one picked by the run seed. */
  noteKeys: z.array(zI18nKey).min(1),
  params: z.record(z.string(), z.unknown()).default({}),
  /** Extra reward on a rewarded repeat play, at most once per the game's `repeatBonusCooldownSec`. */
  repeatBonus: zRewards.optional(),
});

export const zMiniGameDef = z.object({
  id: zMiniGameId,
  nameKey: zI18nKey,
  durationHintSec: z.number().int().positive(),
  levels: z.array(zMiniGameLevel).min(1),
  repeatReward: z.object({ min: z.number().int().min(15), max: z.number().int().positive() }),
  /** Score that pays the maximum repeat reward (dusting 0.92, packing 1.0). */
  repeatScoreFull: z.number().min(0).max(1),
  repeatCooldownSec: z.number().int().positive(),
  /** Play-clock seconds between two `repeatBonus` grants (Bếp của bà: one homemade dish per 5 minutes). */
  repeatBonusCooldownSec: z.number().int().positive().optional(),
});

export type MiniGameDefInput = z.input<typeof zMiniGameDef>;
export type MiniGameDef = z.output<typeof zMiniGameDef>;
export type MiniGameLevelDef = z.output<typeof zMiniGameLevel>;

/** What a mini game module reports back. It never touches the wallet itself. */
export interface MiniGameResult {
  miniGameId: `mg_${string}`;
  runId: `run_${string}`;
  level: number;
  completed: boolean;
  /** 0..1 — dusting: clean ratio at the end (auto-finish = 1). */
  score: number;
  /** Repeat note shown on the result card, remembered for Sổ công thức (must be one of the level's). */
  noteKey?: string;
  /** Actions whose first-time hand hint the player has now seen (Bếp của bà §4.9). */
  seenActions?: readonly string[];
}
